import React, { useState, useEffect } from 'react';
import { getImagePath } from './config/imagePath';

const HolsterVisualizer = ({ 
  gunData, 
  lightData, 
  colorData, 
  mountData, 
  clawData,        
  wingData, 
  hangerData,
  thumbScrewData,
  isLightbearing,
  isLeftHanded, 
  viewSide,
  cantId,
  holsterType = "iwb",
  systemImage = null,
  hasSystemView = false,
  tacBeltColor,
  fulcrumColor,
  thighBandColor,
  hideDefaultScrews = false
}) => {

  const [showSystemView, setShowSystemView] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(true);

  let sysImages = [];
  if (Array.isArray(systemImage)) {
      sysImages = systemImage;
  } else if (systemImage) {
      sysImages = [systemImage];
  }

  const showToggleBtn = hasSystemView || sysImages.length > 0;

  useEffect(() => {
    if (!showToggleBtn) setShowSystemView(false);
  }, [showToggleBtn]);

  useEffect(() => {
    setShowSystemView(false);
  }, [mountData?.id]);


  // ==========================================================================
  // 1. DETERMINE GEOMETRY GROUP
  // ==========================================================================
  const getLightGroup = () => {
    if (isLightbearing !== "yes" || !lightData) return "none";

    const selectedLightId = lightData.id;
    const largeLightTarget = (holsterType === 'owb' || holsterType === 'nidhogg' || holsterType === 'daisho' || holsterType === 'nidhogg_l2') ? 'x300' : 'tlr1';

    const lightImageMap = {
      
      "x300": "x300", "x300u_a": "x300", "x300u_b": "x300", "x300v_a": "x300", "x300v_b": "x300", 
      "x300_t": "x300", "x400": "x300", "x400v_a_irc": "x300", "x400u_a_rd": "x300", "surefire_xvl2": "x300", "xvl2": "x300",
      "modlite_pl350": "x300", "pl350": "x300", "inforce_wild2": "x300", "wild2": "x300",
      "olight_pl_pro": "x300", "olight_pl2": "x300", "inforce_apl": "x300", "inforce_apl_3": "x300",
      "tlr1": largeLightTarget, "tlr1_hl": largeLightTarget, "tlr1_hlx": largeLightTarget,
      "tlr2": largeLightTarget, "gtl_10": largeLightTarget,
      "tlr7": "tlr7", "tlr7_a": "tlr7", "tlr7_x": "tlr7", "tlr7_hlx": "tlr7", "tlr7_a_x": "tlr7",
      "tlr7_sub": "tlr7", "tlr7_sub_1913": "tlr7", "tlr7_sub_glock": "tlr7", "tlr7_sub_p365": "tlr7",
      "tlr6": "tlr7", "tlr6_hl": "tlr7",
      "surefire_xc1b": "tlr7", "surefire_xc1c": "tlr7", "surefire_xc2": "tlr7", "xc2": "tlr7",
      "surefire_xc3": "tlr7", "xc3": "tlr7", "surefire_xsc": "tlr7", "surefire_xsc_a": "tlr7", "surefire_xsc_b": "tlr7", "xsc": "tlr7",
      "inforce_wild1": "tlr7", "wild1": "tlr7",
      "inforce_aplc_glock": "tlr7", "inforce_aplc_uni": "tlr7", "inforce_aplc": "tlr7",
      "olight_pl_mini": "tlr7", "olight_pl_mini2": "tlr7", "sig_foxtrot1x": "tlr7", "sig_foxtrot2r": "tlr7"
    };

    return lightImageMap[selectedLightId] || selectedLightId;
  };

  const currentLightGroup = getLightGroup();

  // ==========================================================================
  // 2. DYNAMIC IMAGE GENERATORS
  // ==========================================================================
  const getShellImage = () => {
    if (!gunData || !colorData || !colorData.id) return null;
    
    let effectiveColorId = colorData.id;

    // --- FIX FOR OWB BACK PANELS ---
    // All OWB back panels render as black
    if (holsterType === 'owb' && viewSide === 'back') {
        effectiveColorId = 'black';
    }

    if (holsterType === 'mag') {
      const depthPrefix = "adj_deep"; 
      return getImagePath(`shells/mag/shell_${depthPrefix}_${effectiveColorId}_${viewSide}.png`);
    }

    if (holsterType === 'rifle_mag') {
        return getImagePath(`shells/mag_rifle/${gunData.id}/shell_none_${effectiveColorId}_${viewSide}.png`);
    }

    let shellLightGroup = currentLightGroup;
    
    if (effectiveColorId === 'frog_skin' && (currentLightGroup === 'tlr1' || lightData?.id === 'tlr1_hl')) {
        shellLightGroup = 'x300';
    }

    return getImagePath(`shells/${holsterType}/shell_${shellLightGroup}_${effectiveColorId}_${viewSide}.png`);
  };

  const getHardwareImage = () => {
    if (hideDefaultScrews) return null; 
    if (!gunData || !colorData) return null;
    if (viewSide === "back") return null;

    if (holsterType === 'revolver') {
        if (!mountData) return null;
        if (mountData.id === 'dcc_mod4' || mountData.id === 'dcc_mod4175') {
            return getImagePath(`overlays/screws_revolver_${mountData.id}.png`);
        }
        return null;
    }

    const groupsWithScrews = ["tlr7", "x300", "tlr1"]; 
    if (!groupsWithScrews.includes(currentLightGroup)) return null; 
    return getImagePath(`overlays/screws_${holsterType}_${currentLightGroup}.png`);
  };

  const getMountImage = () => {
    if (!mountData) return null;

    if (holsterType === 'rifle_mag' && viewSide === 'front') {
        return null;
    }

    if (mountData.id === 'dropleg_qls' || mountData.id === 'dropleg_no_qls') {
        return null;
    }

    let originalPath;
    if (cantId === "10_degree") {
      originalPath = viewSide === "back" 
        ? (mountData.img_layer_mount_back_canted || mountData.img_layer_mount_back) 
        : (mountData.img_layer_mount_canted || mountData.img_layer_mount);
    } else {
      originalPath = viewSide === "back" ? mountData.img_layer_mount_back : mountData.img_layer_mount;
    }
    if (!originalPath) return null;

    // Skip appending the light suffix to direct bolt mounts, they are universal!
    if (holsterType === 'daisho' || holsterType === 'rifle_mag' || holsterType === 'masterblaster' || holsterType === 'revolver' || holsterType === 'mag' || holsterType === 'nidhogg_l2' || mountData.no_light_suffix || mountData.id === 'tac_belt_standard' || mountData.id === 'tac_belt_bolted') {
      return getImagePath(originalPath.replace(/^(\/)?(anr-builder\/)?images\//, ''));
    }

    const filename = originalPath.replace('.png', `_${currentLightGroup}.png`);
    return getImagePath(filename.replace(/^(\/)?(anr-builder\/)?images\//, ''));
  };

  const getClawImage = () => {
    if (holsterType === 'daisho') return null; 
    if (!clawData || clawData.id === "no") return null;
    
    const originalPath = viewSide === "back" ? clawData.img_layer_claw_back : clawData.img_layer_claw_front;
    if (!originalPath) return null;

    if (holsterType === 'masterblaster' || holsterType === 'revolver') {
       return getImagePath(originalPath.replace(/^(\/)?(anr-builder\/)?images\//, ''));
    }

    const filename = originalPath.replace('.png', `_${currentLightGroup}.png`);
    return getImagePath(filename.replace(/^(\/)?(anr-builder\/)?images\//, ''));
  };

  const getDaishoClawImage = () => {
    if (holsterType !== 'daisho') return null;
    
    const colorId = clawData?.id || 'black';
    const colorPrefix = colorId === 'black' ? '' : `_${colorId}`;

    if (currentLightGroup === 'none' && viewSide === 'front') { 
        return getImagePath(`claws/daisho/claw_daisho${colorPrefix}_none_front_v2.png`) + "?v=2"; 
    }
    return getImagePath(`claws/daisho/claw_daisho${colorPrefix}_${currentLightGroup}_${viewSide}.png`) + "?v=2";
  };

  // Cache-buster: wing art is re-shot periodically under the SAME filenames, so
  // browsers/CDNs will serve stale bytes without a version string. Bump this
  // (?v=3, ?v=4 ...) any time wing images are replaced in place.
  const getDaishoWingImage = () => {
    if (holsterType !== 'daisho' || !wingData) return null;
    return getImagePath(`wings/daisho/wing_daisho_${wingData.id}_${viewSide}.png`) + "?v=2";
  };

  // --- HANGER IMAGE ---
  const getHangerImage = () => {
    if (!hangerData || hangerData.id === "no") return null;
    
    const originalPath = viewSide === "back" ? hangerData.img_layer_hanger_back : hangerData.img_layer_hanger_front;
    if (!originalPath) return null;

    return getImagePath(originalPath.replace(/^(\/)?(anr-builder\/)?images\//, ''));
  };

  // --- THUMB SCREW IMAGE ---
  // Renders different overlays per light group (none, tlr7, x300) — front view only
  const getThumbScrewImage = () => {
    if (!thumbScrewData || thumbScrewData.id === "no") return null;
    if (viewSide === "back") return null;
    return getImagePath(`overlays/thumb_screws_nidhogg_${currentLightGroup}_front.png`) + "?v=2";
};
  // Generate all active image paths
  const baseImageSrc = getShellImage();
  const hardwareImageSrc = getHardwareImage(); 
  const clawImageSrc = getClawImage();
  const mountImageSrc = getMountImage();
  const daishoClawSrc = getDaishoClawImage();
  const daishoWingSrc = getDaishoWingImage();
  const hangerImageSrc = getHangerImage();
  const thumbScrewImageSrc = getThumbScrewImage();

  // Create a strict key based ONLY on the final image strings
  const geometryKey = [
      baseImageSrc, hardwareImageSrc, clawImageSrc, mountImageSrc, 
      daishoClawSrc, daishoWingSrc, hangerImageSrc, thumbScrewImageSrc,
      viewSide, showSystemView, sysImages.join(',')
  ].join('|');

  // ONLY trigger the loading screen when the actual images change!
  useEffect(() => {
    setImagesLoading(true);
  }, [geometryKey]);

  const handleImageError = (e) => { 
      e.target.style.display = 'none'; 
      setImagesLoading(false); 
  };
  const handleImageLoaded = () => { setImagesLoading(false); };

  // ==========================================================================
  // 3. LAYER ORDERING LOGIC
  // ==========================================================================
  let shellZIndex = 10; let hardwareZIndex = 15; let clawZIndex = 20; let wingZIndex = 25; let mountZIndex = 30; 
  let hangerZIndex = 5;
  let thumbScrewZIndex = 16; // Sits just above default hardware screws

  // Nidhogg, Nidhogg L2, and OWB naturally place the mount Behind the shell (Z5) on the front, and on top (Z10) on the back!
  if (holsterType === 'owb' || holsterType === 'nidhogg' || holsterType === 'nidhogg_l2' || holsterType === 'rifle_mag') {
    if (viewSide === 'front') { mountZIndex = 5; shellZIndex = 10; hardwareZIndex = 15; thumbScrewZIndex = 16; hangerZIndex = 5; } 
    else { shellZIndex = 5; mountZIndex = 10; hardwareZIndex = 15; thumbScrewZIndex = 16; hangerZIndex = 10; }
  }
  if (holsterType === 'daisho' && viewSide === 'back') { wingZIndex = 5; clawZIndex = 6; }
  if (holsterType === 'revolver' && viewSide === 'front') { hardwareZIndex = 35; }


  const baseImageStyle = {
    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", 
    maxWidth: "90%", opacity: imagesLoading && !showSystemView ? 0 : 1, transition: "opacity 0.4s ease-in-out"
  }

  return (
    <div className="visualizer-box" style={{ width: "100%", backgroundColor: "#1e1e1e", position: "relative", borderRadius: "4px", overflow: "hidden", border: "1px solid #333", aspectRatio: "1 / 1", height: "auto" }}>
      
      {imagesLoading && baseImageSrc && !showSystemView && (
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: "#666", zIndex: 0, fontSize: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Loading...</div>
      )}

      <div style={{
        width: "100%", height: "100%", position: "relative",
        transform: (isLeftHanded && !showSystemView) ? "scaleX(-1)" : "none", 
        transition: "transform 0.5s"
      }}>
        {showSystemView && showToggleBtn ? (
            <div style={{ width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#1e1e1e" }}>
                {sysImages.map((src, index) => (
                    <img 
                        key={src} 
                        src={src} 
                        alt={`Mounting System Layer ${index}`} 
                        onLoad={handleImageLoaded} 
                        onError={handleImageError} 
                        style={{ width: "100%", height: "100%", objectFit: "contain", position: "absolute", top: 0, left: 0, zIndex: 101 + index }} 
                    />
                ))}
                
                {sysImages.length === 0 && (
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", color: "#666", zIndex: 102, fontSize: "12px", textTransform: "uppercase", textAlign: "center", padding: "0 20px" }}>
                        Select mount colors to preview
                    </div>
                )}
            </div>
        ) : (
            <>
                {!baseImageSrc && <img src={getImagePath("builder_hero.png") + "?v=3"} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain", opacity: 1 }} />}
                
                <div key={geometryKey} style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}>
                    {/* Hanger Layer */}
                    {baseImageSrc && hangerImageSrc && <img src={hangerImageSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="Deep Concealment Hanger" style={{ ...baseImageStyle, zIndex: hangerZIndex }} />}
                    
                    {/* Shell Layer */}
                    {baseImageSrc && <img src={baseImageSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="Shell" style={{ ...baseImageStyle, zIndex: shellZIndex }} />}
                    
                    {/* Default Hardware Screws */}
                    {baseImageSrc && hardwareImageSrc && <img src={hardwareImageSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="Hardware" style={{ ...baseImageStyle, pointerEvents: "none", zIndex: hardwareZIndex }} />}
                    
                    {/* Extended Thumb Screws Overlay */}
                    {baseImageSrc && thumbScrewImageSrc && <img src={thumbScrewImageSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="Thumb Screws" style={{ ...baseImageStyle, pointerEvents: "none", zIndex: thumbScrewZIndex }} />}
                    
                    {/* Claw Layers */}
                    {baseImageSrc && clawImageSrc && <img src={clawImageSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="IWB Claw" style={{ ...baseImageStyle, zIndex: clawZIndex }} />}
                    {baseImageSrc && daishoClawSrc && <img src={daishoClawSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="Daisho Claw" style={{ ...baseImageStyle, zIndex: clawZIndex }} />}
                    {baseImageSrc && daishoWingSrc && <img src={daishoWingSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="Daisho Wing" style={{ ...baseImageStyle, zIndex: wingZIndex }} />}
                    
                    {/* Mount Layer */}
                    {baseImageSrc && mountImageSrc && <img src={mountImageSrc} onError={handleImageError} onLoad={handleImageLoaded} alt="Mount" style={{ ...baseImageStyle, zIndex: mountZIndex }} />}
                </div>
            </>
        )}
      </div> 

      <div style={{ position: "absolute", bottom: "15px", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", zIndex: 105, width: "90%" }}>
        {showToggleBtn && (
            <button 
                onClick={() => setShowSystemView(!showSystemView)} 
                className={`system-view-btn ${showSystemView ? 'active' : ''}`} 
                style={{ 
                    background: "rgba(0,0,0,0.8)", color: "#fff", border: "1px solid #D5A678", 
                    borderRadius: "4px", padding: "10px 20px", fontSize: "12px", fontWeight: "bold", 
                    cursor: "pointer", textAlign: "center", transition: "all 0.3s ease", width: "100%", maxWidth: "250px"
                }}
            >
                {showSystemView ? "← RETURN TO HOLSTER" : "VIEW DROPLEG SYSTEM"}
            </button>
        )}
      </div>
    </div>
  );
};

export default HolsterVisualizer;